import { UserManager, WebStorageStateStore, User, UserManagerSettings } from 'oidc-client-ts';
import {
  bindOidcSessionInvalidationEvents,
  OidcSessionInvalidationReason,
} from './oidcSessionEvents';
import {
  clearOidcAuthStorage,
  OidcExplicitLogoutFence,
  refreshOidcUser,
} from './oidcSessionLifecycle';

const getOidcSettings = (): UserManagerSettings => {
  // Determine Keycloak URL based on environment
  // In production, this should be configured via environment variable
  // In Docker, services communicate via container names
  // In local dev, use localhost
  let keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL;
  
  if (!keycloakUrl) {
    // Fallback for different environments
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      keycloakUrl = 'http://localhost:8080';
    } else {
      // For production, use the same protocol and hostname
      keycloakUrl = `${window.location.protocol}//${window.location.hostname}:8080`;
    }
  }
  
  const redirectUri = `${window.location.origin}/auth/callback`;
  const postLogoutRedirectUri = `${window.location.origin}/login`;
  const configuredScope = (import.meta.env.VITE_OIDC_SCOPE as string | undefined)?.trim();
  const scope = configuredScope && configuredScope.length > 0
    ? configuredScope
    : 'openid';
  
  console.log('🔐 OIDC Settings:', {
    keycloakUrl,
    redirectUri,
    postLogoutRedirectUri,
    clientId: import.meta.env.VITE_OIDC_CLIENT_ID || 'recruitment-app',
    scope,
  });
  
  return {
    authority: `${keycloakUrl}/realms/optima`,
    client_id: import.meta.env.VITE_OIDC_CLIENT_ID || 'recruitment-app',
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    response_type: 'code',
    scope,
    // PKCE Configuration (enabled automatically by oidc-client-ts)
    automaticSilentRenew: true,
    silent_redirect_uri: `${window.location.origin}/auth/silent-renew`,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    loadUserInfo: true,
    monitorSession: true,
    revokeTokensOnSignout: true,
    includeIdTokenInSilentRenew: true,
    filterProtocolClaims: true,
  };
};

export class OidcAuthService {
  private userManager: UserManager;
  private readonly sessionInvalidatedListeners = new Set<(
    reason: OidcSessionInvalidationReason,
  ) => void>();
  private invalidationPromise: Promise<void> | null = null;
  private sessionInvalidated = false;
  private readonly explicitLogoutFence = new OidcExplicitLogoutFence();

  constructor() {
    this.userManager = new UserManager(getOidcSettings());
    
    // Set up event handlers
    this.userManager.events.addUserLoaded((user) => {
      this.sessionInvalidated = false;
      if (user.access_token) {
        localStorage.setItem('access_token', user.access_token);
      }
    });

    bindOidcSessionInvalidationEvents(
      this.userManager.events,
      () => this.refreshToken(),
      (reason) => this.invalidateSession(reason),
    );

    this.userManager.events.addUserSignedOut(() => {
      void this.invalidateSession('user-signed-out').finally(() => {
        window.location.href = '/';
      });
    });
  }

  onUserLoaded(listener: (user: User) => void): () => void {
    this.userManager.events.addUserLoaded(listener);
    return () => this.userManager.events.removeUserLoaded(listener);
  }

  onSessionInvalidated(
    listener: (reason: OidcSessionInvalidationReason) => void,
  ): () => void {
    this.sessionInvalidatedListeners.add(listener);
    return () => this.sessionInvalidatedListeners.delete(listener);
  }

  /**
   * Initiate login flow
   */
  async login(returnUrl?: string): Promise<void> {
    try {
      const state = returnUrl ? { returnUrl } : undefined;
      await this.userManager.signinRedirect({ state });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Handle callback after login
   */
  async handleCallback(): Promise<User> {
    try {
      const user = await this.userManager.signinRedirectCallback();
      
      // Store tokens for API calls
      if (user.access_token) {
        localStorage.setItem('access_token', user.access_token);
      }
      
      // Check for return URL in state
      const returnUrl = (user.state as any)?.returnUrl || '/';
      window.history.replaceState({}, document.title, returnUrl);
      
      return user;
    } catch (error) {
      console.error('Callback handling error:', error);
      throw error;
    }
  }

  /**
   * Handle silent renew callback
   */
  async handleSilentCallback(): Promise<void> {
    try {
      await this.userManager.signinSilentCallback();
    } catch (error) {
      console.error('Silent callback error:', error);
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      this.explicitLogoutFence.begin();
      // FIRST: Set logout flags in sessionStorage BEFORE any clearing
      const logTime = new Date().toISOString();
      sessionStorage.setItem('logout_initiated', logTime);
      sessionStorage.setItem('logout_in_progress', 'true');
      console.log('🚪 Starting logout process at:', logTime);
      
      // Get the current user to extract the ID token hint BEFORE clearing anything
      const user = await this.userManager.getUser();
      console.log('🔍 Current user state:', user ? 'User exists' : 'No user');
      sessionStorage.setItem('logout_user_existed', user ? 'true' : 'false');
      
      // Store the ID token before clearing
      const idToken = user?.id_token;
      
      // Construct direct Keycloak logout URL BEFORE clearing storage
      const settings = getOidcSettings();
      const idTokenHint = idToken ? `&id_token_hint=${encodeURIComponent(idToken)}` : '';
      const keycloakLogoutUrl = `${settings.authority}/protocol/openid-connect/logout?client_id=${settings.client_id}&post_logout_redirect_uri=${encodeURIComponent(`${window.location.origin}/login`)}${idTokenHint}`;
      
      // NOW clear authentication data (but preserve logout flags)
      this.clearAuthStorage(true);
      
      // Remove user from OIDC storage
      await this.userManager.removeUser();
      
      // Force redirect to Keycloak logout endpoint
      window.location.href = keycloakLogoutUrl;
      
    } catch (error) {
      console.error('Logout error:', error);
      sessionStorage.setItem('logout_error', String(error));
      
      // Set logout flag BEFORE any clearing
      sessionStorage.setItem('logout_in_progress', 'true');
      
      // Fallback: Clear everything and redirect to login
      try {
        await this.userManager.removeUser();
      } catch (removeError) {
        console.error('Error removing user:', removeError);
      }
      
      // Clear authentication storage (but preserve logout flags)
      this.clearAuthStorage(true);
      
      // Force redirect to login page
      window.location.href = '/login';
    }
  }

  /**
   * Clear all authentication-related storage
   */
  private clearAuthStorage(preserveLogoutFlags: boolean = false): void {
    try {
      clearOidcAuthStorage(localStorage, sessionStorage, preserveLogoutFlags);
    } catch {
      // Storage may be unavailable in privacy-restricted browsers. Redux is
      // still invalidated through the session listener.
    }
  }

  /**
   * Get current user
   */
  async getUser(): Promise<User | null> {
    try {
      const user = await this.userManager.getUser();
      return user;
    } catch (error) {
      console.error('Get user error:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return !!user && !user.expired;
  }

  /**
   * Get access token
   */
  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.access_token || null;
  }

  /**
   * Get user profile
   */
  async getUserProfile(): Promise<any | null> {
    const user = await this.getUser();
    return user?.profile || null;
  }

  /**
   * Refresh token manually
   */
  async refreshToken(): Promise<User> {
    return refreshOidcUser(
      () => this.userManager.signinSilent(),
      () => this.invalidateSession('refresh-failed'),
      (token) => localStorage.setItem('access_token', token),
    );
  }

  private invalidateSession(reason: OidcSessionInvalidationReason): Promise<void> {
    if (this.invalidationPromise) return this.invalidationPromise;
    if (this.sessionInvalidated) return Promise.resolve();
    this.sessionInvalidated = true;

    this.invalidationPromise = (async () => {
      this.clearAuthStorage(
        this.explicitLogoutFence.shouldPreserveFlags(reason),
      );
      for (const listener of this.sessionInvalidatedListeners) {
        try {
          listener(reason);
        } catch {
          // One UI listener must not prevent the remaining mirrors from being
          // invalidated or leave the OIDC user persisted.
        }
      }
      if (reason !== 'user-unloaded') {
        await this.userManager.removeUser();
      }
    })().finally(() => {
      this.invalidationPromise = null;
    });

    return this.invalidationPromise;
  }

  /**
   * Direct login with username/password (fallback)
   */
  async directLogin(username: string, password: string): Promise<any> {
    const authGatewayUrl = import.meta.env.VITE_AUTH_GATEWAY_URL || 'http://localhost:3000';
    
    try {
      const response = await fetch(`${authGatewayUrl}/auth/login/direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      
      // Store tokens
      if (data.accessToken) {
        localStorage.setItem('access_token', data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('refresh_token', data.refreshToken);
      }
      
      return data;
    } catch (error) {
      console.error('Direct login error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const oidcAuth = new OidcAuthService();
export default oidcAuth;
